import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const StaffSidebar = ({ onLogout }) => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) setIsOpen(false);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const navLinks = [
    {
      to: "/staff/dashboard",
      label: "Dashboard",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 3h18v4H3V3zM3 13h18v8H3v-8z"
          />
        </svg>
      ),
    },
    {
      to: "/staff/providers",
      label: "Providers",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1z"
          />
        </svg>
      ),
    },
    {
      to: "/staff/services",
      label: "Services",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"
          />
        </svg>
      ),
    },
  ];

  const staff = JSON.parse(localStorage.getItem("staff") || "{}");

  return (
    <>
      {/* Mobile toggle button */}
      {isMobile && (
        <button
          className="fixed top-4 left-4 z-50 p-2 rounded-md bg-white shadow"
          onClick={() => setIsOpen(!isOpen)}
        >
          <svg
            className="w-5 h-5 text-gray-700"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={isOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
            />
          </svg>
        </button>
      )}

      <aside
        className={`fixed inset-y-0 left-0 w-64 bg-white border-r shadow-sm transform ${
          isMobile
            ? isOpen
              ? "translate-x-0"
              : "-translate-x-full"
            : "translate-x-0"
        } transition-transform duration-200`}
        style={{ zIndex: 60 }}
      >
        {/* Brand + collapse icon */}
        <div className="h-20 px-4 flex items-center justify-between border-b">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-600 flex items-center justify-center text-white font-bold">
              INB
            </div>
            <div>
              <div className="text-gray-900 font-semibold">Admin Console</div>
              <div className="text-xs text-gray-500">Management Portal</div>
            </div>
          </div>
          <button
            className="p-1 bg-gray-100 rounded-md hover:bg-gray-200"
            aria-label="collapse sidebar"
          >
            <svg
              className="w-5 h-5 text-gray-600"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 12H4"
              />
            </svg>
          </button>
        </div>

        {/* Nav */}
        <nav className="p-4 space-y-2">
          {navLinks.map((link) => {
            const isActive =
              location.pathname === link.to ||
              location.pathname.startsWith(link.to + "/");
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition ${
                  isActive
                    ? "bg-blue-600 text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <span
                  className={`p-2 rounded-md ${
                    isActive
                      ? "bg-blue-700 text-white"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {link.icon}
                </span>
                <span className="font-medium">{link.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="mt-auto p-4 border-t">
          <div className="bg-white rounded-lg p-3 flex items-center gap-3 shadow-sm">
            <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold">
              {(staff.name || "S").charAt(0)}
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-gray-900">
                {staff.name || "Staff"}
              </div>
              <div className="text-xs text-gray-500">
                {staff.role || "Staff Member"}
              </div>
            </div>
          </div>

          <button
            onClick={() => {
              if (onLogout) onLogout();
              else {
                localStorage.removeItem("staffToken");
                window.location.href = "/login";
              }
            }}
            className="mt-3 w-full bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg flex items-center gap-2 justify-center hover:bg-red-100"
          >
            <svg
              className="w-4 h-4 text-red-600"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7"
              />
            </svg>
            Logout
          </button>
        </div>
      </aside>
    </>
  );
};

export default StaffSidebar;
